import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFirestore, collection, getDocs, getDoc, doc, updateDoc } from 'firebase/firestore';
import ImageOrderDetails from '../components/Orders/ImageOrderDetails';
import './ordersPanel.scss';

const STATUS_TABS = ['All', 'Pending', 'Due', 'Paid', 'Completed', 'Canceled'];

const STATUS_META = {
  pending: { label: 'Pending', pill: 'op-pill--status-pending' },
  due: { label: 'Due', pill: 'op-pill--status-due' },
  paid: { label: 'Paid', pill: 'op-pill--status-inprogress' },
  completed: { label: 'Completed', pill: 'op-pill--status-completed' },
  canceled: { label: 'Canceled', pill: 'op-pill--status-cancelled' },
};

const normalizeStatus = (value) => {
  if (!value) return '';
  return String(value).toLowerCase().replace(/[-_\s]+/g, '');
};

const getStatusMeta = (status) => STATUS_META[normalizeStatus(status)] || STATUS_META.pending;

const toDateSafe = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp?.toDate === 'function') {
    try {
      return timestamp.toDate();
    } catch (_) {
      return null;
    }
  }
  if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000);
  }
  if (typeof timestamp === 'number') {
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return new Date(ms);
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return null;
};

const formatDate = (value) => {
  const date = toDateSafe(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return '—';
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });
};

const countImages = (order) => {
  if (!order) return 0;
  const sources = [order.images, order.attachments, order.completedAttachments, order.response];
  for (const list of sources) {
    if (Array.isArray(list) && list.length) return list.length;
  }
  return 0;
};

const firstPreviewUrl = (order) => {
  if (!order) return '';
  const candidates = [order.images, order.attachments, order.completedAttachments, order.response]
    .filter(Array.isArray)
    .flat();
  for (const item of candidates) {
    if (!item) continue;
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      if (item.url) return item.url;
      if (item.downloadURL) return item.downloadURL;
    }
  }
  return '';
};

const formatRequestId = (id) => {
  if (!id) return '—';
  const str = String(id);
  if (str.length <= 8) return `#${str}`;
  return `#${str.slice(0, 4)}…${str.slice(-3)}`;
};

const buildPaginationItems = (currentPage, totalPages, maxVisible = 7) => {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1);
  }

  const pages = [1];
  const middleSlots = maxVisible - 2;
  let start = Math.max(2, currentPage - Math.floor(middleSlots / 2));
  let end = start + middleSlots - 1;

  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = end - middleSlots + 1;
  }

  if (start > 2) pages.push("ellipsis-left");
  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    pages.push(pageNum);
  }
  if (end < totalPages - 1) pages.push("ellipsis-right");
  pages.push(totalPages);
  return pages;
};

export default function ImagesPanel() {
  const [artOrders, setArtOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [customerData, setCustomerData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('All');
  const [assetFilter, setAssetFilter] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const db = getFirestore();

  const fetchOrders = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'artOrders'));
      const orders = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          let customerName = 'Unknown Customer';

          if (data.customerId) {
            try {
              const userSnap = await getDoc(doc(db, 'users', data.customerId));
              if (userSnap.exists()) {
                const userData = userSnap.data();
                customerName = userData.freshbooksClientId?.organization
                  ? userData.freshbooksClientId.organization
                  : `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown Customer';
              }
            } catch (err) {
              console.error('Failed to fetch user:', err);
            }
          }

          return { id: docSnap.id, ...data, customerName };
        })
      );
      setArtOrders(orders);
    } catch (err) {
      console.error('Failed to fetch art orders:', err);
    }
  }, [db]);

  const handleRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await fetchOrders();
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchOrders]);

  useEffect(() => {
    const now = Date.now();
    localStorage.setItem('lastVisitedArtOrdersPage', now.toString());
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const ids = new Set(artOrders.map((order) => order.id));
    setSelectedOrders((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next;
    });
  }, [artOrders]);

  const filteredOrders = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    const tabKey = tab === 'All' ? '' : normalizeStatus(tab);

    return artOrders
      .filter((order) => {
        const statusKey = normalizeStatus(order.status);
        const matchesTab = !tabKey || statusKey === tabKey;
        const matchesSearch = !search
          || String(order.id || '').toLowerCase().includes(search)
          || (order.customerName && order.customerName.toLowerCase().includes(search));
        const imagesCount = countImages(order);
        const matchesAssets = !assetFilter
          || (assetFilter === 'withAssets' && imagesCount > 0)
          || (assetFilter === 'withoutAssets' && imagesCount === 0);
        return matchesTab && matchesSearch && matchesAssets;
      })
      .sort((a, b) => {
        const aDate = toDateSafe(a.createdAt)?.getTime() || 0;
        const bDate = toDateSafe(b.createdAt)?.getTime() || 0;
        return sortDirection === 'desc' ? bDate - aDate : aDate - bDate;
      });
  }, [artOrders, searchTerm, tab, assetFilter, sortDirection]);

  const itemsPerPage = 20;
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / itemsPerPage));
  const page = Math.min(currentPage, totalPages);
  const paginatedOrders = filteredOrders.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const allFilteredSelected = filteredOrders.length > 0 && filteredOrders.every((order) => selectedOrders.has(order.id));

  const toggleSelectOrder = (orderId, checked) => {
    setSelectedOrders((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(orderId);
      } else {
        next.delete(orderId);
      }
      return next;
    });
  };

  const selectAllFiltered = (checked) => {
    if (!checked) {
      setSelectedOrders(new Set());
      return;
    }
    const next = new Set(filteredOrders.map((order) => order.id));
    setSelectedOrders(next);
  };

  const handleOpenDetails = useCallback(async (order) => {
    if (order.customerId) {
      try {
        const userSnap = await getDoc(doc(db, 'users', order.customerId));
        if (userSnap.exists()) {
          setCustomerData(userSnap.data());
        } else {
          setCustomerData({});
        }
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        setCustomerData({});
      }
    } else {
      setCustomerData({});
    }

    setSelectedOrder(order);
  }, [db]);

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  const handleStatusChange = async (eventOrId, maybeStatus) => {
    let orderId;
    let newStatus;

    if (typeof eventOrId === 'object' && eventOrId.target) {
      newStatus = eventOrId.target.value;
      orderId = selectedOrder?.id;
    } else {
      orderId = eventOrId;
      newStatus = maybeStatus;
    }

    if (!newStatus || !orderId) return;

    try {
      const orderRef = doc(db, 'artOrders', orderId);
      await updateDoc(orderRef, { status: newStatus });

      setSelectedOrder((prev) => (prev ? { ...prev, status: newStatus } : prev));
      setArtOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkNote = () => {
    if (!selectedOrders.size) return;
    alert('Bulk image actions coming soon.');
  };

  const handleOpenPreview = (order, event) => {
    event.stopPropagation();
    const url = firstPreviewUrl(order);
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <div className="op-page">
      <div className="op-shell">
        <header className="op-header">
          <h1>Image Requests</h1>
          <div className="op-header__right">
            <button className="op-btn" type="button">Export</button>
            <button
              className="op-btn"
              type="button"
              onClick={handleBulkNote}
              disabled={selectedOrders.size === 0}
            >
              More actions{selectedOrders.size ? ` • Selected (${selectedOrders.size})` : ''}
            </button>
            <button className="op-btn op-btn--primary" type="button">New request</button>
          </div>
        </header>

        {!selectedOrder ? (
          <>
            <div className="op-tabs">
              {STATUS_TABS.map((status) => (
                <button
                  key={status}
                  className={`op-tab ${tab === status ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => {
                    setTab(status);
                    setCurrentPage(1);
                  }}
                >
                  {status}
                </button>
              ))}
            </div>

            <div className="op-toolbar">
              <div className="op-toolbar__left">
                <button onClick={handleRefresh} className="op-btn op-btn--ghost" type="button" disabled={isRefreshing}>
                  {isRefreshing ? 'Refreshing…' : 'Refresh'}
                </button>
                <div className="op-search">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path
                      fill="currentColor"
                      d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 5 1.5-1.5-5-5zM9.5 14C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
                    />
                  </svg>
                  <input
                    placeholder="Search by order or customer"
                    value={searchTerm}
                    onChange={(event) => {
                      setSearchTerm(event.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="op-toolbar__right">
                <select
                  value={assetFilter}
                  onChange={(event) => {
                    setAssetFilter(event.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All requests</option>
                  <option value="withAssets">With images</option>
                  <option value="withoutAssets">Needs images</option>
                </select>
                <select
                  value={sortDirection}
                  onChange={(event) => {
                    setSortDirection(event.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="desc">Newest first</option>
                  <option value="asc">Oldest first</option>
                </select>
              </div>
            </div>

            <div
              className="op-table op-table--images"
              style={{
                '--op-col-order': '110px',
                '--op-col-date': '150px',
                '--op-col-customer': '220px',
                '--op-col-total': '110px',
                '--op-col-items': '80px',
                '--op-col-status': '140px',
                '--op-col-payment': '130px',
                '--op-col-action': '90px',
              }}
            >
              <div className="op-head">
                <div className="op-cell op-cell--check">
                  <input
                    type="checkbox"
                    aria-label="select all"
                    checked={allFilteredSelected}
                    onChange={(event) => selectAllFiltered(event.target.checked)}
                  />
                </div>
                <div className="op-cell op-cell--order">Request</div>
                <div className="op-cell op-cell--date">Submitted</div>
                <div className="op-cell op-cell--customer">Customer</div>
                <div className="op-cell op-cell--total">Quote</div>
                <div className="op-cell op-cell--items">Images</div>
                <div className="op-cell op-cell--status">Status</div>
                <div className="op-cell op-cell--payment">Payment</div>
                <div className="op-cell op-cell--action">Action</div>
              </div>

              {paginatedOrders.length === 0 ? (
                <div className="op-row">
                  <div className="op-cell" style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#64748b' }}>
                    No image requests match the current filters.
                  </div>
                </div>
              ) : (
                paginatedOrders.map((order) => {
                  const statusMeta = getStatusMeta(order.status);
                  const statusKey = normalizeStatus(order.status);
                  const isPaid = statusKey !== 'pending' && statusKey !== 'due';
                  const imagesCount = countImages(order);
                  const hasAssets = imagesCount > 0;
                  const previewUrl = firstPreviewUrl(order);
                  // Override status display if paid and has assets
                  const displayStatus = (statusKey === 'paid' && hasAssets)
                    ? { label: 'Ready', pill: 'op-pill--status-downloaded' }
                    : statusMeta;

                  return (
                    <div
                      key={order.id}
                      className="op-row"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenDetails(order)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenDetails(order);
                        }
                      }}
                    >
                      <div className="op-cell op-cell--check">
                        <input
                          type="checkbox"
                          aria-label="select row"
                          checked={selectedOrders.has(order.id)}
                          onChange={(event) => toggleSelectOrder(order.id, event.target.checked)}
                          onClick={(event) => event.stopPropagation()}
                        />
                      </div>
                      <div className="op-cell op-cell--order">{formatRequestId(order.id)}</div>
                      <div className="op-cell op-cell--date">{formatDate(order.createdAt)}</div>
                      <div className="op-cell op-cell--customer">{order.customerName || order.customerId || '—'}</div>
                      <div className="op-cell op-cell--total">{formatCurrency(order.total ?? order.price ?? order.amount)}</div>
                      <div className="op-cell op-cell--items">{imagesCount || '—'}</div>
                      <div className="op-cell op-cell--status">
                        <span className={`op-pill ${displayStatus.pill}`}>{displayStatus.label}</span>
                      </div>
                      <div className="op-cell op-cell--payment">
                        <span className={`op-pill ${isPaid ? 'op-pill--pay-paid' : 'op-pill--pay-unpaid'}`}>
                          {isPaid ? 'Paid' : 'Unpaid'}
                        </span>
                      </div>
                      <div className="op-cell op-cell--action">
                        <button
                          className="op-icon"
                          type="button"
                          aria-label="open details"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenDetails(order);
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 110-10 5 5 0 010 10z"
                            />
                          </svg>
                        </button>
                        <button
                          className="op-icon"
                          type="button"
                          aria-label="open first asset"
                          disabled={!previewUrl}
                          onClick={(event) => handleOpenPreview(order, event)}
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16">
                            <path
                              fill="currentColor"
                              d="M5 20h14v-2H5v2zm7-15l-5 5h3v4h4v-4h3l-5-5z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}

              <div className="op-pagination">
                {buildPaginationItems(page, totalPages).map((item) => {
                  if (typeof item !== "number") {
                    return (
                      <span key={item} className="op-pagination__ellipsis">
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={item}
                      type="button"
                      className={page === item ? "active" : ""}
                      onClick={() => setCurrentPage(item)}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <ImageOrderDetails
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
            handleCloseDetails={handleCloseDetails}
            handleStatusChange={handleStatusChange}
            db={db}
            customerData={customerData}
            refreshOrders={fetchOrders}
          />
        )}
      </div>
    </div>
  );
}
